<template>
  <div class="profile-settings space-y-6">
    <div class="flex items-center justify-between">
      <h3 class="text-xl font-semibold text-theme-secondary leading-none">
        Profile Settings
      </h3>
    </div>

    <form
      @submit.prevent="updateProfile"
      class="max-w-2xl space-y-6 bg-white p-6 rounded-xl"
    >
      <!-- Name Input -->
       <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
         <div class="form-item">
           <label for="first_name" class="block text-sm font-medium text-theme-body mb-2"
             >First Name</label
           >
           <input
             type="text"
             id="first_name"
             v-model="form.first_name"
             placeholder="Enter your name"
             class="w-full px-4 py-2.5 border border-theme-border rounded-lg focus:ring-2 focus:ring-theme-primary focus:border-transparent outline-none transition-all"
             :class="{ 'border-red-500': isSubmit && v$.first_name.$error }"
           />
           <p v-if="isSubmit && v$.first_name.$error" class="text-xs text-red-500 mt-1">
             First name is required.
           </p>
         </div>
         <div class="form-item">
           <label for="last_name" class="block text-sm font-medium text-theme-body mb-2"
             >Last Name</label
           >
           <input
             type="text"
             id="last_name"
             autocomplete="last-name"
             v-model="form.last_name"
             placeholder="Enter your last name"
             class="w-full px-4 py-2.5 border border-theme-border rounded-lg focus:ring-2 focus:ring-theme-primary focus:border-transparent outline-none transition-all"
           />
         </div>

       </div>

      <!-- Email Input -->
      <div class="form-item opacity-70">
        <label
          for="email"
          class="block text-sm font-medium text-theme-body mb-2"
          >Email Address</label
        >
        <input
          type="email"
          id="email"
          v-model="form.email"
          disabled
          class="w-full px-4 py-2.5 bg-gray-50 border border-theme-border rounded-lg cursor-not-allowed"
        />
      </div>

      <div class="border-t border-theme-border border-opacity-40 pt-6">
        <h4
          class="text-sm font-semibold text-theme-secondary mb-4 uppercase tracking-wider"
        >
           {{ summary.pass_check ? 'Set Password' : 'Change Password' }}
        </h4>
        <div class="form-item mb-2" v-if="!summary.pass_check">
          <label
            for="current_password"
            class="block text-sm font-medium text-theme-body mb-2"
            >Current Password</label
          >
          <input
            type="password"
            id="current_password"
            v-model="form.current_password"
            placeholder="••••••••"
            class="w-full px-4 py-2.5 border border-theme-border rounded-lg focus:ring-2 focus:ring-theme-primary focus:border-transparent outline-none transition-all"
          />
          <p v-if="password_error || (isSubmit && v$.current_password.$error)" class="text-xs text-red-500 mt-1">
            {{ password_error || 'Current password is required.' }}
          </p>
        </div>
        <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
          <!-- Password Input -->
          <div class="form-item">
            <label
              for="password"
              class="block text-sm font-medium text-theme-body mb-2"
              >New Password</label
            >
            <input
              type="password"
              id="password"
              autocomplete="new-password"
              v-model="form.password"
              placeholder="••••••••"
              class="w-full px-4 py-2.5 border border-theme-border rounded-lg focus:ring-2 focus:ring-theme-primary focus:border-transparent outline-none transition-all"
              :class="{ 'border-red-500': isSubmit && v$.password.$error }"
            />
            <p
              v-if="isSubmit && v$.password.$error"
              class="text-xs text-red-500 mt-1"
            >
              {{ v$.password.$errors[0].$message }}
            </p>
          </div>

          <!-- Confirm Password Input -->
          <div class="form-item">
            <label
              for="password_confirmation"
              class="block text-sm font-medium text-theme-body mb-2"
              >Confirm Password</label
            >
            <input
              type="password"
              id="password_confirmation"
              v-model="form.password_confirmation"
              placeholder="••••••••"
              class="w-full px-4 py-2.5 border border-theme-border rounded-lg focus:ring-2 focus:ring-theme-primary focus:border-transparent outline-none transition-all"
              :class="{
                'border-red-500': isSubmit && v$.password_confirmation.$error,
              }"
            />
            <p
              v-if="isSubmit && v$.password_confirmation.$error"
              class="text-xs text-red-500 mt-1"
            >
              Passwords must match.
            </p>
          </div>
        </div>
        <p class="text-xs text-theme-body-light mt-2">
          Leave blank if you don't want to change your password.
        </p>
      </div>

      <div class="pt-4">
        <Button
          type="submit"
          title="Save Changes"
          :loader="loading"
          class="w-full md:w-auto px-12"
        />
      </div>
    </form>
  </div>
</template>

<script setup>
import { ref, reactive, inject, onMounted, computed } from "vue";
import { useStore } from "vuex";
import useVuelidate from "@vuelidate/core";
import { required, minLength, sameAs, requiredIf } from "@vuelidate/validators";
import AccountSummary from "@/modules/AccountSummary";

const axios = inject("axios");
const store = useStore();

const summary = computed(() => store.getters.coins);

const { coins } = AccountSummary();

const loading = ref(false);
const isSubmit = ref(false);
let password_error = ref("");

const form = reactive({
  first_name: "",
  last_name: "",
  email: localStorage.getItem("email") || "",
  current_password: "",
  password: "",
  password_confirmation: "",
});

const rules = {
  first_name: { required },
  current_password: {
    required: requiredIf(computed(() => {
      return !summary.value.pass_check && (!!form.password || !!form.password_confirmation);
    }))
  },
  password: { required: requiredIf(computed(() => !!form.current_password)), minLength: minLength(8) },
  password_confirmation: {
    required: requiredIf(computed(() => !!form.password)),
    sameAsPassword: sameAs(computed(() => form.password)),
  },
};

const v$ = useVuelidate(rules, form);

onMounted(() => {
  // Optionally fetch fresh user data here if needed
  const userId = localStorage.getItem("user_id");
  if (userId) {
    axios.api
      .get(`/account/get-user/${localStorage.getItem("email")}`)
      .then((res) => {
        if (res.data.status) {
          form.first_name = res.data.data.name;
          form.last_name = res.data.data.last_name;
        }
      });
  }
});

const updateProfile = async () => {
  isSubmit.value = true;
  password_error.value = "";
  const result = await v$.value.$validate();
  if (!result) return;

  loading.value = true;
  const userId = localStorage.getItem("user_id");

  try {
    const payload = {
      name: form.first_name,
      last_name: form.last_name,
    };

    if (form.password || form.current_password) {
      payload.current_password = form.current_password;
      payload.password = form.password;
      payload.password_confirmation = form.password_confirmation;
    }

    const res = await axios.authApi.post(
      `/account/update-user/${userId}`,
      payload,
    );

    if (res.data.status) {
      if (form.password){
        coins();
      }
      localStorage.setItem("username", form.first_name);
      store.dispatch("setToast", {
        status: true,
        msg: "Profile updated successfully!",
        type: "success",
      });
      // Clear password fields
      form.current_password = "";
      form.password = "";
      form.password_confirmation = "";
      isSubmit.value = false;
    } else {
      if(res.data.error == 'Current password is incorrect.'){
        password_error.value = res.data.error;
      }
      store.dispatch("setToast", {
        status: true,
        msg: "Failed to update profile",
        type: "danger",
      });
    }
  } catch (error) {
    store.dispatch("setToast", {
      status: true,
      msg: "An error occurred during update",
      type: "danger",
    });
  } finally {
    loading.value = false;
  }
};
</script>

<style scoped>
.form-item input:disabled {
  background-color: #f9fafb;
}
</style>
